import React, { useMemo, useState } from 'react';
import { View, Pressable } from 'react-native';
import Text from '@/components/Typography/Text';
import { useTheme } from '@/context/ThemeContext';
import { useUIStore } from '@/store/ui';
import { useCoworkerStore } from '@/store/coworker';
import { latestWorkArtifactRevision } from '@/lib/workArtifactRevision';
import { filesApi } from '@/lib/api/files';
import { openMessageFile } from '@/constants/utils/files';
import { flushEditorSaves } from '@/lib/editorSaveRegistry';

const REVISION_VIEWERS = new Set(['code-preview', 'pdf-preview', 'pptx-preview', 'spreadsheet-editor', 'document-editor', 'presentation-editor']);

export default function WorkArtifactRevisionNotice() {
  const { colors } = useTheme();
  const props = useUIStore(state => state.contextSidebarProps);
  const type = useUIStore(state => state.contextSidebarType);
  const source = props?.sourceFile || props?.file;
  const events = useCoworkerStore(state => state.transcriptEventsByTask);
  const latest = useMemo(() => latestWorkArtifactRevision(events, source), [events, source]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  if (!latest || !REVISION_VIEWERS.has(type)) return null;
  const openLatest = async () => {
    setLoading(true);
    setError(false);
    try {
      const saves = await flushEditorSaves();
      if (saves.some(result => result === false)) {
        setError('Your current edits could not be saved. Retry before opening the latest version.');
        return;
      }
      const file = await filesApi.fetchFile(latest.file.id || latest.file._id);
      // A slow download must not reopen a viewer the user has since closed
      // or replace a different file they selected in the meantime.
      const shouldOpen = () => {
        const ui = useUIStore.getState();
        return ui.isContextSidebarVisible && ui.contextSidebarProps === props;
      };
      if (!shouldOpen()) return;
      await openMessageFile(file, { shouldOpen, throwOnError: true });
    } catch (_) {
      setError('Couldn’t open the latest version. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  return (
    <View style={{ padding: 12, gap: 6, borderBottomWidth: 1, borderBottomColor: colors.border_color_secondary }}>
      <Text size="s" weight="semibold">A newer version is available</Text>
      <Text size="xs" color="secondary">You’re viewing an earlier version. Open the latest output to see the agent’s changes.</Text>
      {error ? <Text size="xs">{error}</Text> : null}
      <Pressable accessibilityRole="button" accessibilityLabel="Open latest artifact version" disabled={loading} onPress={openLatest}>
        <Text size="s" weight="semibold">{loading ? 'Opening…' : 'Open latest version'}</Text>
      </Pressable>
    </View>
  );
}
